import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AdminBar } from '@/components/AdminBar';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import {
  Loader2, ChevronDown, ChevronUp, Eye, EyeOff, Play, Info,
  User, Users, Tractor, Quote, ShieldAlert, X, MapPin, CalendarRange, FlaskConical,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('biounica_token')}` });

// I media sono serviti dal backend su /api/media: in produzione è la stessa
// origine della SPA, ma risolverli sul backend evita sorprese se cambia.
const mediaUrl = (u) =>
  u && u.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u;

const PROF_LABELS = {
  medico: 'Medico',
  veterinario: 'Veterinario',
  naturopata: 'Naturopata',
  operatore_olistico: 'Operatore olistico',
  erborista_parafarmacia: 'Erborista / parafarmacia',
  informatore_farmaceutico: 'Informatore farmaceutico',
  marketer_influencer: 'Marketer / influencer',
};

const SUBJECTS = [
  { value: 'tutti', label: 'Tutti', icon: Users },
  { value: 'umano', label: 'Persone', icon: User },
  { value: 'animali_affezione', label: 'Animali d’affezione', icon: Users },
  { value: 'allevamento', label: 'Allevamento', icon: Tractor },
];

const SUBJECT_LABELS = {
  umano: 'Persone',
  animali_affezione: 'Animali d’affezione',
  allevamento: 'Allevamento',
};

const initials = (name = '') =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join('').toUpperCase();

/* ============ MEDIA ============ */

function Lightbox({ image, onClose }) {
  return (
    <Dialog open={!!image} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl p-0 bg-black border-none overflow-hidden">
        <button
          onClick={onClose}
          className="absolute right-3 top-3 z-10 rounded-full bg-black/60 p-2 text-white hover:bg-black/80"
          aria-label="Chiudi"
        >
          <X className="w-4 h-4" />
        </button>
        {image && (
          <>
            <img src={mediaUrl(image.url)} alt={image.caption || ''} className="w-full max-h-[75vh] object-contain" />
            {image.caption && (
              <p className="px-4 py-3 text-xs text-white/70 text-center">{image.caption}</p>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function GalleryBlock({ block, onOpen }) {
  const [revealed, setRevealed] = useState(!block.sensitive);
  const beforeAfter = block.layout === 'before_after';

  return (
    <figure className="my-5" data-testid="feedback-gallery">
      {block.title && (
        <figcaption className="text-[11px] font-semibold uppercase tracking-wider text-[#4a4238] mb-2">
          {block.title}
        </figcaption>
      )}

      {!revealed ? (
        <button
          onClick={() => setRevealed(true)}
          className="w-full rounded-xl border border-dashed border-[#4a4238]/30 bg-gray-50 px-4 py-6 text-center transition-colors hover:bg-gray-100"
          data-testid="reveal-sensitive"
        >
          <ShieldAlert className="w-5 h-5 mx-auto mb-2 text-[#4a4238]" />
          <p className="text-xs font-medium text-[#12100c]">
            {block.images?.length || 0} immagini non mostrate
          </p>
          <p className="text-[11px] text-[#4a4238] mt-1">
            {block.note || 'Contenuto esplicito.'} Tocca per visualizzarle.
          </p>
          <span className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-medium text-[#4a5f43]">
            <Eye className="w-3.5 h-3.5" /> Mostra le immagini
          </span>
        </button>
      ) : (
        <>
          <div className={beforeAfter ? 'grid grid-cols-2 gap-2' : 'grid grid-cols-2 sm:grid-cols-3 gap-2'}>
            {(block.images || []).map((img) => (
              <button
                key={img.url}
                onClick={() => onOpen(img)}
                className="group relative overflow-hidden rounded-xl bg-gray-100 aspect-[3/4]"
                data-testid="gallery-thumb"
              >
                <img
                  src={mediaUrl(img.url)}
                  alt={img.caption || ''}
                  loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                />
                {img.caption && (
                  <span className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1.5 text-left text-[10px] font-medium text-white">
                    {img.caption}
                  </span>
                )}
              </button>
            ))}
          </div>
          {block.sensitive && (
            <button
              onClick={() => setRevealed(false)}
              className="mt-2 inline-flex items-center gap-1.5 text-[11px] text-[#4a4238] hover:text-[#12100c]"
            >
              <EyeOff className="w-3.5 h-3.5" /> Nascondi le immagini
            </button>
          )}
        </>
      )}
    </figure>
  );
}

function VideoBlock({ block, feedback }) {
  const [playing, setPlaying] = useState(false);
  const videoId = feedback.video_id;
  const poster = feedback.video_poster;

  if (!videoId) {
    return (
      <div className="my-5 rounded-xl border border-dashed border-[#4a4238]/30 bg-gray-50 px-4 py-6 text-center">
        <Play className="w-5 h-5 mx-auto mb-2 text-[#4a4238]" />
        <p className="text-xs font-medium text-[#12100c]">{block.title || 'Video'}</p>
        <p className="text-[11px] text-[#4a4238] mt-1">In fase di caricamento, disponibile a breve.</p>
      </div>
    );
  }

  const src = `https://api.vadoo.tv/iframe_test?id=${videoId}`;
  const fallbackPoster = `https://image.vadoo.tv/MetaDigitale_b46a4c96-a9c7-4ff9-9487-0d11c2acf376/videos/${videoId}/Thumbnails/${videoId}.0000000.jpg`;

  return (
    <figure className="my-5" data-testid="feedback-video">
      {block.title && (
        <figcaption className="text-[11px] font-semibold uppercase tracking-wider text-[#4a4238] mb-2">
          {block.title}
        </figcaption>
      )}
      <div className="relative mx-auto w-full max-w-[320px] overflow-hidden rounded-2xl bg-black aspect-[9/16]">
        {playing ? (
          <iframe
            src={src}
            title={block.title || 'Video'}
            allow="autoplay; fullscreen"
            allowFullScreen
            scrolling="no"
            className="absolute inset-0 h-full w-full border-0"
          />
        ) : (
          <button
            onClick={() => setPlaying(true)}
            className="group absolute inset-0 h-full w-full"
            aria-label="Riproduci il video"
          >
            <img
              src={mediaUrl(poster) || fallbackPoster}
              alt=""
              loading="lazy"
              className="h-full w-full object-cover opacity-90 transition-opacity group-hover:opacity-100"
            />
            <span className="absolute inset-0 flex items-center justify-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-white/90 shadow-lg transition-transform group-hover:scale-110">
                <Play className="ml-0.5 h-6 w-6 fill-[#12100c] text-[#12100c]" />
              </span>
            </span>
          </button>
        )}
      </div>
    </figure>
  );
}

/* ============ CORPO A BLOCCHI ============ */

function BodyBlocks({ body, feedback, onOpenImage }) {
  if (!Array.isArray(body) || body.length === 0) {
    return <p className="text-sm leading-relaxed text-[#12100c]">{feedback.feedback_text}</p>;
  }

  return (
    <div className="space-y-3">
      {body.map((b, i) => {
        switch (b.type) {
          case 'h':
            return (
              <h3 key={i} className="font-heading text-sm font-bold text-[#12100c] pt-2">
                {b.text}
              </h3>
            );
          case 'ul':
            return (
              <ul key={i} className="space-y-1.5 pl-1">
                {(b.items || []).map((it, j) => (
                  <li key={j} className="flex gap-2 text-sm leading-relaxed text-[#12100c]">
                    <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[#c6a15b]" />
                    <span>{it}</span>
                  </li>
                ))}
              </ul>
            );
          case 'quote':
            return (
              <blockquote
                key={i}
                className="my-4 rounded-xl border-l-[3px] border-[#c6a15b] bg-[#c6a15b]/5 py-3 pl-4 pr-3"
              >
                <Quote className="mb-1.5 h-3.5 w-3.5 text-[#8a6620]" />
                <p className="text-sm italic leading-relaxed text-[#12100c]">{b.text}</p>
              </blockquote>
            );
          case 'note':
            return (
              <div key={i} className="my-4 flex gap-2.5 rounded-xl bg-gray-50 px-3.5 py-3">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#4a4238]" />
                <p className="text-xs leading-relaxed text-[#4a4238]">{b.text}</p>
              </div>
            );
          case 'gallery':
            return <GalleryBlock key={i} block={b} onOpen={onOpenImage} />;
          case 'video':
            return <VideoBlock key={i} block={b} feedback={feedback} />;
          case 'p':
          default:
            return (
              <p key={i} className="text-sm leading-relaxed text-[#12100c]">
                {b.text}
              </p>
            );
        }
      })}
    </div>
  );
}

/* ============ CARD ============ */

function FeedbackCard({ fb, onOpenImage }) {
  const [open, setOpen] = useState(false);
  const hasBody = Array.isArray(fb.body) && fb.body.length > 0;
  const hasNumbers = Array.isArray(fb.key_numbers) && fb.key_numbers.length > 0;
  const meta = [
    // La numerosità è quasi sempre già il primo numero chiave: non ripeterla.
    !hasNumbers && fb.sample_size && { icon: Users, text: fb.sample_size },
    fb.observation_period && { icon: CalendarRange, text: fb.observation_period },
    fb.location && { icon: MapPin, text: fb.location },
  ].filter(Boolean);

  return (
    <Card
      className={`overflow-hidden rounded-2xl shadow-sm ${
        fb.is_featured ? 'border-[#c6a15b]/40 ring-1 ring-[#c6a15b]/20' : 'border-gray-100'
      }`}
      data-testid={`feedback-${fb.id}`}
    >
      <CardContent className="p-5 sm:p-6">
        {/* intestazione */}
        <div className="flex items-start gap-3">
          {fb.avatar_url ? (
            <img
              src={mediaUrl(fb.avatar_url)}
              alt=""
              className="h-11 w-11 shrink-0 rounded-full object-cover ring-1 ring-gray-100"
            />
          ) : (
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-[#12100c]/8 text-[13px] font-bold text-[#12100c]">
              {fb.is_anonymous ? <FlaskConical className="h-5 w-5" strokeWidth={1.5} /> : initials(fb.professional_name)}
            </div>
          )}

          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span className="text-sm font-semibold text-[#12100c]">{fb.professional_name}</span>
              <Badge variant="outline" className="text-[9px] font-medium">
                {PROF_LABELS[fb.professional_type] || fb.professional_type}
              </Badge>
              {fb.subject && SUBJECT_LABELS[fb.subject] && (
                <Badge variant="outline" className="border-[#5f7d52]/25 text-[9px] font-medium text-[#4a5f43]">
                  {SUBJECT_LABELS[fb.subject]}
                </Badge>
              )}
              {fb.product_line === 'umivet' && (
                <Badge className="border-none bg-[#12100c] text-[9px] font-medium text-white">UMIVET</Badge>
              )}
              {fb.is_featured && (
                <Badge className="border-none bg-[#c6a15b] text-[9px] font-medium text-[#12100c]">In evidenza</Badge>
              )}
            </div>
            {fb.role_title && (
              <p className="mt-1 text-[11px] leading-snug text-[#4a4238]">{fb.role_title}</p>
            )}
          </div>
        </div>

        {/* titolo e sintesi */}
        {fb.subtitle && (
          <h2 className="font-heading mt-4 text-base font-bold leading-snug text-[#12100c] sm:text-lg">
            {fb.subtitle}
          </h2>
        )}
        {fb.summary && <p className="mt-2 text-sm leading-relaxed text-[#4a4238]">{fb.summary}</p>}

        {/* numeri chiave */}
        {hasNumbers && (
          <div className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-4">
            {fb.key_numbers.map((n, i) => (
              <div key={i} className="rounded-xl bg-[#12100c]/[0.04] px-3 py-2.5">
                <p className="font-heading text-base font-bold leading-none text-[#12100c]">{n.value}</p>
                <p className="mt-1 text-[10px] leading-tight text-[#4a4238]">{n.label}</p>
              </div>
            ))}
          </div>
        )}

        {/* meta */}
        {meta.length > 0 && (
          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
            {meta.map((m, i) => {
              const Icon = m.icon;
              return (
                <span key={i} className="inline-flex items-center gap-1.5 text-[11px] text-[#4a4238]">
                  <Icon className="h-3 w-3" strokeWidth={1.5} /> {m.text}
                </span>
              );
            })}
          </div>
        )}

        {/* relazione completa */}
        {hasBody && (
          <>
            <button
              onClick={() => setOpen((v) => !v)}
              className="mt-4 inline-flex items-center gap-1.5 rounded-full border border-[#12100c]/15 px-3.5 py-1.5 text-[11px] font-medium text-[#12100c] transition-colors hover:bg-[#12100c]/5"
              data-testid={`toggle-${fb.id}`}
              aria-expanded={open}
            >
              {open ? (
                <>
                  Chiudi la relazione <ChevronUp className="h-3.5 w-3.5" />
                </>
              ) : (
                <>
                  Leggi la relazione completa <ChevronDown className="h-3.5 w-3.5" />
                </>
              )}
            </button>

            {open && (
              <div className="mt-4 border-t border-gray-100 pt-4">
                <BodyBlocks body={fb.body} feedback={fb} onOpenImage={onOpenImage} />
                {fb.editorial_note && (
                  <p className="mt-5 border-t border-dashed border-gray-200 pt-3 text-[11px] italic leading-relaxed text-[#4a4238]">
                    {fb.editorial_note}
                  </p>
                )}
                {fb.area_tags?.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-1.5">
                    {fb.area_tags.map((t) => (
                      <Badge key={t} variant="outline" className="text-[9px] font-normal">
                        {t}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

/* ============ PAGINA ============ */

export default function FeedbackPage() {
  const navigate = useNavigate();
  const [feedbacks, setFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subject, setSubject] = useState('tutti');
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/feedbacks`, { headers: authHeaders() })
      .then((r) => setFeedbacks(Array.isArray(r.data) ? r.data : []))
      .catch(() => setFeedbacks([]))
      .finally(() => setLoading(false));
  }, []);

  const available = useMemo(() => {
    const present = new Set(feedbacks.map((f) => f.subject).filter(Boolean));
    return SUBJECTS.filter((s) => s.value === 'tutti' || present.has(s.value));
  }, [feedbacks]);

  const visible = useMemo(
    () => (subject === 'tutti' ? feedbacks : feedbacks.filter((f) => f.subject === subject)),
    [feedbacks, subject],
  );

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-[#12100c]" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl" data-testid="feedback-page">
      <div className="mb-5">
        <h1 className="font-heading text-2xl font-bold text-[#12100c] sm:text-3xl">
          Feedback dei professionisti
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-[#4a4238]">
          Relazioni ed esperienze dirette di colleghi che usano UMIFUL nella pratica quotidiana:
          naturopati, veterinari e allevamenti. Le relazioni sugli animali sono spesso le più utili
          con chi diffida degli integratori, perché in un allevamento non esiste effetto placebo.
        </p>
      </div>

      {/* avvertenza d'uso */}
      <div
        className="mb-5 flex gap-3 rounded-2xl border border-[#c6a15b]/30 bg-[#c6a15b]/[0.07] px-4 py-3.5"
        data-testid="feedback-disclaimer"
      >
        <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-[#8a6620]" />
        <div>
          <p className="text-xs font-semibold text-[#12100c]">Come si usano questi contenuti</p>
          <p className="mt-1 text-xs leading-relaxed text-[#4a4238]">
            Sono racconti personali di singoli professionisti, non studi clinici e non
            caratteristiche del prodotto: descrivono ciò che ciascun autore dichiara di avere
            osservato e non costituiscono prova di efficacia. Materiale riservato ai promotori,
            utile a voi per capire come i colleghi impiegano UMIFUL. Non va riprodotto né citato
            nella comunicazione ai clienti o al pubblico.
          </p>
        </div>
      </div>

      <AdminBar
        section="feedbacks"
        onAdd={() => navigate('/admin', { state: { tab: 'feedbacks', action: 'new' } })}
      />

      {/* filtri */}
      {available.length > 2 && (
        <div className="mb-4 flex flex-wrap gap-2" data-testid="feedback-filters">
          {available.map((s) => {
            const Icon = s.icon;
            const count =
              s.value === 'tutti'
                ? feedbacks.length
                : feedbacks.filter((f) => f.subject === s.value).length;
            const active = subject === s.value;
            return (
              <button
                key={s.value}
                onClick={() => setSubject(s.value)}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-xs font-medium transition-all ${
                  active
                    ? 'border-[#12100c] bg-[#12100c] text-white'
                    : 'border-gray-200 bg-white text-[#4a4238] hover:border-[#12100c]/30'
                }`}
                data-testid={`filter-${s.value}`}
              >
                <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                {s.label}
                <span className={active ? 'text-white/60' : 'text-[#4a4238]/50'}>{count}</span>
              </button>
            );
          })}
        </div>
      )}

      {visible.length === 0 ? (
        <Card className="rounded-2xl border-gray-100 shadow-sm">
          <CardContent className="p-8 text-center">
            <p className="text-sm text-[#4a4238]">
              {feedbacks.length === 0
                ? 'Nessun feedback ancora pubblicato. I feedback dei professionisti saranno disponibili a breve.'
                : 'Nessun feedback in questa categoria.'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {visible.map((fb) => (
            <FeedbackCard key={fb.id} fb={fb} onOpenImage={setLightbox} />
          ))}
        </div>
      )}

      <Lightbox image={lightbox} onClose={() => setLightbox(null)} />
    </div>
  );
}
